import React, { useEffect, useState } from "react";
import { Table, Input, Select } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import DeleteModal from "../../../components/modelpopup/DeleteModal";
import AddTDS from "./AddTDS";
import { deleteArrears } from "../Arrears/store/arrearsSlice";
import { fetchTDS } from "./store/tdsSlice";
import api from "../../../api";

const { Option } = Select;

const TDSDataTable = ({ data, loading }) => {
  const [selectedFilters, setSelectedFilters] = useState({
    year: null,
    months: [],
    search: "",
  });
  const [debouncedSearch, setDebouncedSearch] = useState(""); // State for debounced search input
  const [years, setYears] = useState([]);
  const [months, setMonths] = useState([]);

  const [selectedEmployeeId, setSelectedEmployeeId] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  const dispatch = useDispatch();
  const { successMessage, error } = useSelector((state) => state.arrearsData);

  useEffect(() => {
    if (successMessage) {
      toast.success(successMessage);
    }
    if (error) {
      toast.error(error);
    }
  }, [successMessage, error]);

  // Debounce search input
  useEffect(() => {
    const handler = setTimeout(() => {
      updateFilters({ search: debouncedSearch });
    }, 500); // 500ms debounce delay

    return () => {
      clearTimeout(handler); // Clear timeout on input change
    };
  }, [debouncedSearch]);

  // Fetch year options on component mount
  useEffect(() => {
    const fetchYearOptions = async () => {
      try {
        const response = await api.post("/emp-pay-slip-year-list");
        setYears(response.data.data);
      } catch (err) {
        toast.error("Failed to fetch year options.");
      }
    };

    fetchYearOptions();
  }, []);

  // Fetch month options when a year is selected
  useEffect(() => {
    if (selectedFilters.year) {
      const fetchMonthOptions = async () => {
        try {
          const response = await api.post("/emp-pay-slip-month-list", {
            year: selectedFilters.year,
          });
          const monthsData = response.data.data || {};
          setMonths(
            Object.entries(monthsData).map(([value, name]) => ({
              value,
              name,
            }))
          );
        } catch (err) {
          console.error("API Error:", err);
          toast.error("Failed to fetch month options.");
        }
      };

      fetchMonthOptions();
    } else {
      setMonths([]); // Reset months if no year is selected
    }
  }, [selectedFilters.year]);

  // Update filters and fetch data
  const updateFilters = (updatedValues) => {
    const newFilters = { ...selectedFilters, ...updatedValues };

    // Transform year and months to the desired format
    const transformedFilters = {
      selectedYear: newFilters.year ? [newFilters.year] : [], // Wrap year in an array
      selectedMonth: newFilters.months.map((month) => +month.value), // Extract only month values as numbers
      search: newFilters.search || "", // Include search term
    };

    setSelectedFilters(newFilters); // Update the original state with full objects
    dispatch(fetchTDS(transformedFilters)); // Send transformed filters to the API
  };

  const handleDelete = (tdsId) => {
    dispatch(deleteArrears(tdsId));
    setDeleteModalOpen(false);
  };

  const columns = [
    {
      title: "Employee Name",
      dataIndex: "employee",
      render: (employee) => `${employee.first_name} ${employee.last_name}`,
    },
    {
      title: "Employee Code",
      dataIndex: "employee_code",
    },
    {
      title: "Amount",
      dataIndex: "amount",
    },
    {
      title: "Month",
      dataIndex: "month",
    },
  ];

  return (
    <div className="row">
      <div className="col-md-12">
        <div className="d-flex justify-content-end mb-4">
          <Select
            placeholder="Select Year"
            style={{ width: 200, marginRight: 10 }}
            onChange={(value) => {
              updateFilters({ year: value, months: [] }); // Reset months when year changes
            }}
            value={selectedFilters.year}
          >
            {years.map((year) => (
              <Option key={year} value={year}>
                {year}
              </Option>
            ))}
          </Select>
          <Select
            mode="multiple"
            placeholder="Select Month(s)"
            style={{ width: 200, marginRight: 10 }}
            value={selectedFilters.months.map((month) => month.value)}
            onChange={(values) => {
              const selected = values.map((value) =>
                months.find((month) => month.value === value)
              );
              updateFilters({ months: selected });
            }}
            disabled={!selectedFilters.year}
          >
            {months.map((month) => (
              <Option key={month.value} value={month.value}>
                {month.name}
              </Option>
            ))}
          </Select>
          <Input
            placeholder="Search Emp Name, Employee Code"
            style={{ width: 200 }}
            onChange={(e) => {
              setDebouncedSearch(e.target.value); // Update debounced search state
            }}
          />
        </div>
        <div className="table-responsive">
          <Table
            columns={columns}
            dataSource={data}
            loading={loading}
            rowKey="id"
            pagination={{
              pageSize: 10,
              showSizeChanger: false,
            }}
          />
          {isModalOpen && (
            <AddTDS
              show={isModalOpen}
              onClose={() => setIsModalOpen(false)}
              userId={selectedEmployeeId}
            />
          )}

          {deleteModalOpen && (
            <DeleteModal
              onConfirm={() => handleDelete(selectedEmployeeId)}
              onCancel={() => setDeleteModalOpen(false)}
              Name="Employee"
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default TDSDataTable;
